import { useRef, useState } from "react";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { dropIndex, moveItem } from "@/lib/printOrder";

/**
 * The selected articles in print order, reorderable (2026-10-01).
 *
 * Drag a row by its grip, or use the up/down buttons. Dragging uses
 * pointer events with pointer capture rather than HTML5 drag-and-drop,
 * so it works the same with a mouse, a trackpad and a finger on the
 * iPad (HTML5 DnD doesn't fire from touch in Safari). While dragging,
 * the list previews the new order; the move is committed on release.
 *
 * Props:
 *   articles — [{id, headline}] in the current print order
 *   onMove(from, to) — commit a move (final indices)
 *   badge(article) — optional node shown after the headline
 */
export default function PrintOrderList({ articles, onMove, badge }) {
  const listRef = useRef(null);
  // { from, over } while a row is being dragged.
  const [drag, setDrag] = useState(null);

  const ids = articles.map((a) => a.id);
  const shownIds = drag ? moveItem(ids, drag.from, drag.over) : ids;
  const byId = new Map(articles.map((a) => [a.id, a]));
  const shown = shownIds.map((id) => byId.get(id)).filter(Boolean);
  const draggedId = drag ? ids[drag.from] : null;

  const rowRects = () =>
    Array.from(listRef.current?.querySelectorAll("[data-print-order-row]") || []).map(
      (el) => el.getBoundingClientRect(),
    );

  const onPointerDown = (e, index) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best effort */
    }
    setDrag({ from: index, over: index });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const over = dropIndex(e.clientY, rowRects(), drag.over);
    if (over !== drag.over) setDrag({ ...drag, over });
  };

  const finish = () => {
    if (drag && drag.over !== drag.from) onMove(drag.from, drag.over);
    setDrag(null);
  };

  return (
    <ol
      ref={listRef}
      data-testid="print-order-list"
      className="mt-2 text-xs border border-[var(--border)] divide-y divide-[var(--border)]"
    >
      {shown.map((a, i) => {
        const dragging = a.id === draggedId;
        return (
          <li
            key={a.id}
            data-print-order-row
            data-testid={`print-order-row-${a.id}`}
            className={
              "flex items-start gap-1.5 px-1.5 py-1.5 " +
              (dragging ? "bg-[var(--secondary)] shadow-lg relative z-10" : "bg-white")
            }
          >
            <button
              type="button"
              aria-label={`Drag to reorder: ${a.headline || "untitled"}`}
              title="Drag to change the print order"
              data-testid={`print-order-grip-${a.id}`}
              onPointerDown={(e) => onPointerDown(e, ids.indexOf(a.id))}
              onPointerMove={onPointerMove}
              onPointerUp={finish}
              onPointerCancel={() => setDrag(null)}
              className="shrink-0 mt-0.5 text-[var(--ink-muted)] hover:text-[var(--ink)] cursor-grab active:cursor-grabbing touch-none"
            >
              <GripVertical className="w-3.5 h-3.5" />
            </button>
            <span className="shrink-0 w-5 text-right font-mono-display text-[var(--ink-muted)]">
              {i + 1}.
            </span>
            <span className="min-w-0 flex-1 text-[var(--ink)] leading-snug">
              {a.headline || "(untitled)"}
              {badge ? badge(a) : null}
            </span>
            <span className="shrink-0 flex flex-col">
              <button
                type="button"
                aria-label="Move up"
                data-testid={`print-order-up-${a.id}`}
                disabled={!!drag || i === 0}
                onClick={() => onMove(i, i - 1)}
                className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-30"
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                aria-label="Move down"
                data-testid={`print-order-down-${a.id}`}
                disabled={!!drag || i === shown.length - 1}
                onClick={() => onMove(i, i + 1)}
                className="text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-30"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
